import { useEffect, useRef, useState, useCallback, useContext } from "react";
import { io } from "socket.io-client";
import Peer from "simple-peer";
import { CurrentUserContext } from "App";
import FALLBACK_ICE_SERVERS, { fetchIceServers } from "utils/iceServers";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3080";

const CALL_STATE = {
  IDLE: "idle",
  WAITING: "waiting",
  CONNECTING: "connecting",
  IN_CALL: "in_call",
  DISCONNECTED: "disconnected",
};

export default function ConnectPage() {
  const { currentUser } = useContext(CurrentUserContext);

  // State
  const [callState, setCallState] = useState(CALL_STATE.IDLE);
  const [stream, setStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCamOff, setIsCamOff] = useState(false);
  const [isSpeakerOff, setIsSpeakerOff] = useState(false);
  const [queuePosition, setQueuePosition] = useState(null);
  const [queueList, setQueueList] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");

  // Refs
  const myVideoRef = useRef();
  const remoteVideoRef = useRef();
  const peerRef = useRef(null);
  const socketRef = useRef(null);
  const streamRef = useRef(null);
  const iceServersRef = useRef(FALLBACK_ICE_SERVERS);

  // Initialize media stream
  const initMedia = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      setStream(mediaStream);
      streamRef.current = mediaStream;
      if (myVideoRef.current) {
        myVideoRef.current.srcObject = mediaStream;
      }
      setErrorMsg("");
    } catch (err) {
      console.error("Failed to get media:", err);
      // Give a specific, actionable error based on the failure reason
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setErrorMsg(
          "Camera/mic permission denied. Please click the camera icon in your browser's address bar, allow access, then reload this page."
        );
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        setErrorMsg(
          "No camera or microphone found. Please connect a camera/mic and reload this page."
        );
      } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
        setErrorMsg(
          "Camera/mic is already in use by another app or browser tab. Close other apps using the camera, then reload this page."
        );
      } else if (err.name === "OverconstrainedError") {
        setErrorMsg(
          "Camera does not support the required settings. Try a different camera or reload."
        );
      } else {
        setErrorMsg(
          `Camera/mic error: ${err.message || err.name || "Unknown error"}. Please reload and try again.`
        );
      }
    }
  }, []);

  // Clean up peer connection
  const destroyPeer = useCallback(() => {
    if (peerRef.current) {
      peerRef.current.destroy();
      peerRef.current = null;
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
  }, []);

  // Initialize socket and media on mount
  useEffect(() => {
    initMedia();

    // Fetch TURN credentials from Metered.ca
    fetchIceServers().then((servers) => {
      iceServersRef.current = servers;
      console.log("ICE servers loaded:", servers.length, "entries",
        servers.some(s => s.urls?.toString().includes("turn:")) ? "(TURN available)" : "(STUN only)"
      );
    });

    const socket = io(API_URL, { autoConnect: false });
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("Connected to socket:", socket.id);
      // Register this user for direct calls
      if (currentUser?.user_id) {
        socket.emit("registerUser", currentUser.user_id);
      }
    });

    socket.on("queuePosition", (position) => {
      setQueuePosition(position);
      setCallState(CALL_STATE.WAITING);
    });

    socket.on("updateQueue", (list) => {
      setQueueList(list);
    });

    // Receive a WebRTC signal (offer or answer or ICE candidate)
    socket.on("webrtc-signal", ({ from, signal }) => {
      if (peerRef.current) {
        // We already have a peer - feed the signal (answer or ICE)
        peerRef.current.signal(signal);
      } else {
        // No peer yet - incoming offer, create answering peer
        setCallState(CALL_STATE.CONNECTING);
        const peer = new Peer({
          initiator: false,
          trickle: true,
          stream: streamRef.current,
          config: { iceServers: iceServersRef.current },
        });

        peer.on("signal", (data) => {
          socket.emit("webrtc-signal", { target: from, signal: data });
        });

        peer.on("stream", (remoteStream) => {
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = remoteStream;
          }
          setCallState(CALL_STATE.IN_CALL);
        });

        peer.on("close", () => {
          setCallState(CALL_STATE.DISCONNECTED);
          destroyPeer();
        });

        peer.on("error", (err) => {
          console.error("Peer error (answerer):", err);
          setErrorMsg(`Connection failed: ${err.message || err}`);
          setCallState(CALL_STATE.DISCONNECTED);
          destroyPeer();
        });

        peer.on("connect", () => {
          console.log("Peer connected (answerer)!");
        });

        peer.on("iceStateChange", (state) => {
          console.log("ICE state (answerer):", state);
        });

        peerRef.current = peer;
        peer.signal(signal);
      }
    });

    // Server matched us with another user - we should initiate the call
    socket.on("matched", ({ partnerId }) => {
      setCallState(CALL_STATE.CONNECTING);
      const peer = new Peer({
        initiator: true,
        trickle: true,
        stream: streamRef.current,
        config: { iceServers: iceServersRef.current },
      });

      peer.on("signal", (data) => {
        socket.emit("webrtc-signal", { target: partnerId, signal: data });
      });

      peer.on("stream", (remoteStream) => {
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = remoteStream;
        }
        setCallState(CALL_STATE.IN_CALL);
      });

      peer.on("close", () => {
        setCallState(CALL_STATE.DISCONNECTED);
        destroyPeer();
      });

      peer.on("error", (err) => {
        console.error("Peer error (initiator):", err);
        setErrorMsg(`Connection failed: ${err.message || err}`);
        setCallState(CALL_STATE.DISCONNECTED);
        destroyPeer();
      });

      peer.on("connect", () => {
        console.log("Peer connected (initiator)!");
      });

      peer.on("iceStateChange", (state) => {
        console.log("ICE state (initiator):", state);
      });

      peerRef.current = peer;
    });

    socket.on("partnerDisconnected", () => {
      setCallState(CALL_STATE.DISCONNECTED);
      destroyPeer();
    });

    socket.on("callEnded", () => {
      setCallState(CALL_STATE.DISCONNECTED);
      destroyPeer();
    });

    return () => {
      destroyPeer();
      socket.off("connect");
      socket.off("queuePosition");
      socket.off("updateQueue");
      socket.off("webrtc-signal");
      socket.off("matched");
      socket.off("partnerDisconnected");
      socket.off("callEnded");
      socket.disconnect();
      socketRef.current = null;
      // Stop media tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, [initMedia, destroyPeer, currentUser?.token]);

  // Assign local stream to video ref when stream changes
  useEffect(() => {
    if (myVideoRef.current && stream) {
      myVideoRef.current.srcObject = stream;
    }
  }, [stream]);

  // Toggle mic
  const toggleMute = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsMuted((prev) => !prev);
    }
  }, []);

  // Toggle camera
  const toggleCam = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getVideoTracks().forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsCamOff((prev) => !prev);
    }
  }, []);

  // Toggle speaker (remote video muted)
  const toggleSpeaker = useCallback(() => {
    if (remoteVideoRef.current) {
      remoteVideoRef.current.muted = !remoteVideoRef.current.muted;
    }
    setIsSpeakerOff((prev) => !prev);
  }, []);

  // Join the queue
  const handleConnect = useCallback(() => {
    if (!socketRef.current) return;
    if (!streamRef.current) {
      setErrorMsg(
        "Camera/mic not available. Check if another tab or app is using the camera, allow permissions in your browser, then reload this page."
      );
      return;
    }
    socketRef.current.connect();
    const username = currentUser?.username || "anonymous";
    const userId = currentUser?.user_id;
    socketRef.current.emit("joinQueue", { username, userId });
    setCallState(CALL_STATE.WAITING);
    setErrorMsg("");
  }, [currentUser]);

  // Skip to next person
  const handleNext = useCallback(() => {
    destroyPeer();
    if (socketRef.current) {
      socketRef.current.emit("nextUser");
    }
    setCallState(CALL_STATE.WAITING);
  }, [destroyPeer]);

  // End call / leave
  const handleEndCall = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.emit("hangup", {
        callEnder: socketRef.current.id,
        target: null, // server handles target lookup
      });
    }
    destroyPeer();
    setCallState(CALL_STATE.DISCONNECTED);
  }, [destroyPeer]);

  // Reconnect from disconnected state
  const handleReconnect = useCallback(() => {
    destroyPeer();
    setErrorMsg("");
    setCallState(CALL_STATE.IDLE);
  }, [destroyPeer]);

  // Render the center overlay content based on state
  const renderOverlay = () => {
    switch (callState) {
      case CALL_STATE.IDLE:
        return (
          <div style={styles.overlayCenter}>
            <div style={styles.welcomeIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#b997ff" strokeWidth="1.5">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <h2 style={styles.welcomeTitle}>Welcome to Disbound</h2>
            <p style={styles.welcomeSubtitle}>Connect with someone new</p>
            <button style={styles.connectBtn} onClick={handleConnect}>
              Start Connecting
            </button>
          </div>
        );
      case CALL_STATE.WAITING:
        return (
          <div style={styles.overlayCenter}>
            <div style={styles.spinner} />
            <p style={styles.waitingText}>
              Looking for someone...
              {queuePosition && <span style={styles.queueBadge}>#{queuePosition} in queue</span>}
            </p>
          </div>
        );
      case CALL_STATE.CONNECTING:
        return (
          <div style={styles.overlayCenter}>
            <div style={styles.spinner} />
            <p style={styles.waitingText}>Connecting...</p>
          </div>
        );
      case CALL_STATE.DISCONNECTED:
        return (
          <div style={styles.overlayCenter}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#b997ff" strokeWidth="1.5" style={{ marginBottom: "1rem" }}>
              <path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
              <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" />
            </svg>
            <h3 style={styles.disconnectedTitle}>Call Disconnected</h3>
            <p style={styles.disconnectedSub}>Ready for another connection?</p>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button style={styles.connectBtn} onClick={handleConnect}>
                Find Someone New
              </button>
              <button style={styles.secondaryBtn} onClick={handleReconnect}>
                Back to Home
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="connect-page" style={styles.page}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @media (max-width: 768px) {
          #main-content-area:has(.connect-page) {
            padding-left: 0 !important;
            padding-right: 0 !important;
            padding-top: 0 !important;
          }
          .connect-page {
            width: 100% !important;
            min-width: 0 !important;
            border-radius: 0 !important;
            height: calc(100vh - 56px - 72px - env(safe-area-inset-top) - env(safe-area-inset-bottom)) !important;
          }
        }
      `}</style>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#b997ff" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <span style={styles.headerTitle}>DISBOUND CONNECT</span>
        </div>
        {callState === CALL_STATE.IN_CALL && (
          <span style={styles.liveBadge}>LIVE</span>
        )}
      </div>

      {/* Error message */}
      {errorMsg && (
        <div style={styles.errorBanner}>{errorMsg}</div>
      )}

      {/* Main video area */}
      <div style={styles.videoContainer}>
        {/* Remote video (full area) */}
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          style={{
            ...styles.remoteVideo,
            display: callState === CALL_STATE.IN_CALL ? "block" : "none",
          }}
        />

        {/* Overlay for non-in-call states */}
        {callState !== CALL_STATE.IN_CALL && (
          <div style={styles.videoOverlay}>
            {renderOverlay()}
          </div>
        )}

        {/* Local video (picture-in-picture) */}
        {stream && (
          <div style={styles.pipContainer}>
            <video
              ref={myVideoRef}
              autoPlay
              playsInline
              muted
              style={{
                ...styles.pipVideo,
                opacity: isCamOff ? 0.3 : 1,
              }}
            />
            {isCamOff && (
              <div style={styles.camOffOverlay}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                  <path d="M1 1l22 22M21 21H3a2 2 0 01-2-2V8a2 2 0 012-2h3m3-3h6l2 3h4a2 2 0 012 2v9.34" />
                </svg>
              </div>
            )}
          </div>
        )}

        {/* Queue info overlay */}
        {queueList.length > 0 && callState === CALL_STATE.WAITING && (
          <div style={styles.queueOverlay}>
            <p style={styles.queueTitle}>In Queue ({queueList.length})</p>
            {queueList.slice(0, 5).map((u, i) => (
              <div key={i} style={styles.queueItem}>
                <span style={styles.queuePos}>#{u.position}</span>
                <span style={styles.queueName}>{u.username}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Controls bar */}
      <div style={styles.controls}>
        <button
          style={{
            ...styles.controlBtn,
            backgroundColor: isSpeakerOff ? "rgba(185,151,255,0.3)" : "rgba(255,255,255,0.1)",
          }}
          onClick={toggleSpeaker}
          title={isSpeakerOff ? "Unmute speaker" : "Mute speaker"}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isSpeakerOff ? "#ff6666" : "#fff"} strokeWidth="2">
            {isSpeakerOff ? (
              <>
                <path d="M11 5L6 9H2v6h4l5 4V5z" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </>
            ) : (
              <>
                <path d="M11 5L6 9H2v6h4l5 4V5z" />
                <path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" />
              </>
            )}
          </svg>
        </button>

        <button
          style={{
            ...styles.controlBtn,
            backgroundColor: isCamOff ? "rgba(185,151,255,0.3)" : "rgba(255,255,255,0.1)",
          }}
          onClick={toggleCam}
          title={isCamOff ? "Turn camera on" : "Turn camera off"}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isCamOff ? "#ff6666" : "#fff"} strokeWidth="2">
            {isCamOff ? (
              <>
                <path d="M1 1l22 22" />
                <path d="M21 21H3a2 2 0 01-2-2V8a2 2 0 012-2h3m3-3h6l2 3h4a2 2 0 012 2v9.34" />
              </>
            ) : (
              <>
                <path d="M23 7l-7 5 7 5V7z" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </>
            )}
          </svg>
        </button>

        <button
          style={{
            ...styles.controlBtn,
            backgroundColor: isMuted ? "rgba(185,151,255,0.3)" : "rgba(255,255,255,0.1)",
          }}
          onClick={toggleMute}
          title={isMuted ? "Unmute mic" : "Mute mic"}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isMuted ? "#ff6666" : "#fff"} strokeWidth="2">
            {isMuted ? (
              <>
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6" />
                <path d="M17 16.95A7 7 0 015 12m14 0a7 7 0 01-.11 1.23" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </>
            ) : (
              <>
                <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
                <path d="M19 10v2a7 7 0 01-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </>
            )}
          </svg>
        </button>

        {/* End call / Next buttons - only show when active */}
        {(callState === CALL_STATE.IN_CALL || callState === CALL_STATE.WAITING || callState === CALL_STATE.CONNECTING) && (
          <button
            style={styles.endCallBtn}
            onClick={handleEndCall}
            title="End call"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
              <path d="M10.68 13.31a16 16 0 003.41 2.6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7 2 2 0 011.72 2v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.42 19.42 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          </button>
        )}

        {callState === CALL_STATE.IN_CALL && (
          <button
            style={styles.nextBtn}
            onClick={handleNext}
            title="Next person"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    maxHeight: "100%",
    backgroundColor: "#101010",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0.75rem 1.25rem",
    borderBottom: "1px solid rgba(185,151,255,0.15)",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  headerTitle: {
    color: "#b997ff",
    fontSize: "0.85rem",
    fontWeight: "700",
    letterSpacing: "0.1em",
  },
  liveBadge: {
    backgroundColor: "#ff4444",
    color: "#fff",
    fontSize: "0.7rem",
    fontWeight: "700",
    padding: "0.15rem 0.5rem",
    borderRadius: "4px",
    letterSpacing: "0.05em",
  },
  errorBanner: {
    backgroundColor: "rgba(255,102,102,0.15)",
    color: "#ff6666",
    padding: "0.5rem 1rem",
    fontSize: "0.85rem",
    textAlign: "center",
  },
  videoContainer: {
    flex: 1,
    position: "relative",
    backgroundColor: "#1a1528",
    margin: "0.5rem",
    borderRadius: "12px",
    overflow: "hidden",
    minHeight: "200px",
    width: "auto",
  },
  remoteVideo: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    position: "absolute",
    top: 0,
    left: 0,
  },
  videoOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "radial-gradient(ellipse at center, rgba(50,43,64,0.9) 0%, rgba(16,16,16,0.95) 100%)",
  },
  overlayCenter: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: "1.5rem 1rem",
    width: "100%",
    boxSizing: "border-box",
  },
  welcomeIcon: {
    marginBottom: "1rem",
    opacity: 0.8,
  },
  welcomeTitle: {
    color: "#fff",
    fontSize: "1.5rem",
    fontWeight: "600",
    marginBottom: "0.5rem",
  },
  welcomeSubtitle: {
    color: "rgba(255,255,255,0.5)",
    fontSize: "0.9rem",
    marginBottom: "1.5rem",
  },
  connectBtn: {
    backgroundColor: "#b997ff",
    color: "#101010",
    border: "none",
    borderRadius: "24px",
    padding: "0.7rem 2rem",
    fontSize: "0.95rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  secondaryBtn: {
    backgroundColor: "transparent",
    color: "#b997ff",
    border: "1px solid #b997ff",
    borderRadius: "24px",
    padding: "0.7rem 1.5rem",
    fontSize: "0.95rem",
    fontWeight: "500",
    cursor: "pointer",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "3px solid rgba(185,151,255,0.2)",
    borderTop: "3px solid #b997ff",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: "1rem",
  },
  waitingText: {
    color: "rgba(255,255,255,0.7)",
    fontSize: "0.95rem",
  },
  queueBadge: {
    display: "inline-block",
    marginLeft: "0.5rem",
    backgroundColor: "rgba(185,151,255,0.2)",
    color: "#b997ff",
    padding: "0.1rem 0.5rem",
    borderRadius: "8px",
    fontSize: "0.8rem",
  },
  disconnectedTitle: {
    color: "#fff",
    fontSize: "1.3rem",
    fontWeight: "600",
    marginBottom: "0.25rem",
  },
  disconnectedSub: {
    color: "rgba(255,255,255,0.5)",
    fontSize: "0.85rem",
    marginBottom: "1.25rem",
  },
  pipContainer: {
    position: "absolute",
    bottom: "1rem",
    right: "0.75rem",
    width: "120px",
    height: "90px",
    borderRadius: "10px",
    overflow: "hidden",
    border: "2px solid rgba(185,151,255,0.4)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
    zIndex: 10,
  },
  pipVideo: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  camOffOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.6)",
  },
  queueOverlay: {
    position: "absolute",
    top: "0.5rem",
    right: "0.5rem",
    backgroundColor: "rgba(0,0,0,0.7)",
    borderRadius: "8px",
    padding: "0.5rem",
    minWidth: "auto",
    maxWidth: "min(140px, 40vw)",
    zIndex: 10,
  },
  queueTitle: {
    color: "#b997ff",
    fontSize: "0.75rem",
    fontWeight: "600",
    marginBottom: "0.5rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  queueItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.2rem 0",
  },
  queuePos: {
    color: "rgba(185,151,255,0.7)",
    fontSize: "0.75rem",
    fontWeight: "600",
  },
  queueName: {
    color: "rgba(255,255,255,0.8)",
    fontSize: "0.8rem",
  },
  controls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.75rem",
    padding: "0.75rem 1rem",
    backgroundColor: "rgba(0,0,0,0.3)",
    flexWrap: "wrap",
    width: "100%",
    boxSizing: "border-box",
  },
  controlBtn: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  endCallBtn: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    backgroundColor: "#ff4444",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  nextBtn: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    backgroundColor: "rgba(185,151,255,0.3)",
    border: "1px solid rgba(185,151,255,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s",
  },
};
